import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AlertFilters, Alert, Equipment } from '@/types'
import { X } from 'lucide-react'

interface AlertFiltersPanelProps {
  filters: AlertFilters
  onFiltersChange: (filters: Partial<AlertFilters>) => void
  onClearFilters: () => void
}

const priorityOptions: { value: Alert['priority']; label: string }[] = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'warning', label: 'Warning' },
  { value: 'info', label: 'Info' }
]

const statusOptions: { value: Alert['status']; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'work-order-created', label: 'Work Order Created' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'false-positive', label: 'False Positive' }
]

const equipmentTypeOptions: { value: Equipment['type']; label: string }[] = [
  { value: 'hvac', label: 'HVAC' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'elevators', label: 'Elevators' },
  { value: 'security', label: 'Security' },
  { value: 'other', label: 'Other' }
]

export default function AlertFiltersPanel({ 
  filters, 
  onFiltersChange, 
  onClearFilters 
}: AlertFiltersPanelProps) {
  const handlePriorityChange = (priority: Alert['priority']) => {
    const current = filters.priority || []
    const updated = current.includes(priority)
      ? current.filter(p => p !== priority)
      : [...current, priority]
    onFiltersChange({ priority: updated })
  }

  const handleStatusChange = (status: Alert['status']) => {
    const current = filters.status || []
    const updated = current.includes(status)
      ? current.filter(s => s !== status)
      : [...current, status]
    onFiltersChange({ status: updated })
  }

  const handleEquipmentTypeChange = (type: Equipment['type']) => {
    const current = filters.equipmentType || []
    const updated = current.includes(type)
      ? current.filter(t => t !== type)
      : [...current, type]
    onFiltersChange({ equipmentType: updated })
  }

  const hasActiveFilters = () => {
    return (filters.priority?.length || 0) > 0 ||
           (filters.status?.length || 0) > 0 ||
           (filters.equipmentType?.length || 0) > 0 ||
           !!filters.propertyId
  }

  const getPriorityVariant = (priority: Alert['priority']): 'critical' | 'warning' | 'info' | 'default' => {
    switch (priority) {
      case 'critical': return 'critical'
      case 'high': return 'critical'
      case 'warning': return 'warning'
      case 'info': return 'info'
      default: return 'default'
    }
  }

  const getStatusVariant = (status: Alert['status']): 'success' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'resolved': return 'success'
      case 'acknowledged': return 'warning'
      case 'work-order-created': return 'info'
      case 'new': return 'default'
      default: return 'default'
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters() && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClearFilters}
            className="flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Clear All
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Priority Filter */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Priority</h4>
          <div className="space-y-2">
            {priorityOptions.map((option) => (
              <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.priority?.includes(option.value) || false}
                  onChange={() => handlePriorityChange(option.value)}
                  className="rounded border-gray-300 text-trust-blue focus:ring-trust-blue"
                />
                <Badge variant={getPriorityVariant(option.value)} className="text-xs">
                  {option.label}
                </Badge>
              </label>
            ))}
          </div>
        </div>

        {/* Status Filter */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Status</h4>
          <div className="space-y-2">
            {statusOptions.map((option) => (
              <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.status?.includes(option.value) || false}
                  onChange={() => handleStatusChange(option.value)}
                  className="rounded border-gray-300 text-trust-blue focus:ring-trust-blue"
                />
                <Badge variant={getStatusVariant(option.value)} className="text-xs">
                  {option.label}
                </Badge>
              </label>
            ))}
          </div>
        </div>

        {/* Equipment Type Filter */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Equipment Type</h4>
          <div className="space-y-2">
            {equipmentTypeOptions.map((option) => (
              <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.equipmentType?.includes(option.value) || false}
                  onChange={() => handleEquipmentTypeChange(option.value)}
                  className="rounded border-gray-300 text-trust-blue focus:ring-trust-blue"
                />
                <span className="text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters() && (
        <div className="pt-4 border-t">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Active Filters:</h4>
          <div className="flex flex-wrap gap-2">
            {filters.priority?.map((priority) => (
              <Badge
                key={`priority-${priority}`}
                variant={getPriorityVariant(priority)}
                className="cursor-pointer"
                onClick={() => handlePriorityChange(priority)}
              >
                {priority ? priority.charAt(0).toUpperCase() + priority.slice(1) : 'Unknown'}
                <X className="w-3 h-3 ml-1" />
              </Badge>
            ))}
            {filters.status?.map((status) => (
              <Badge
                key={`status-${status}`}
                variant={getStatusVariant(status)}
                className="cursor-pointer"
                onClick={() => handleStatusChange(status)}
              >
                {status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                <X className="w-3 h-3 ml-1" />
              </Badge>
            ))}
            {filters.equipmentType?.map((type) => (
              <Badge
                key={`equipment-${type}`}
                variant="default"
                className="cursor-pointer"
                onClick={() => handleEquipmentTypeChange(type)}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
                <X className="w-3 h-3 ml-1" />
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}